import * as Popover from "@radix-ui/react-popover";
import { useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import { useNavigate } from "@tanstack/react-router";
import { ChevronDown, Plus } from "lucide-react";
import { useState } from "react";
import { cn, useTranslation } from "@/packages/utils";
import { Trans } from "react-i18next";
import { CreateWorkspaceModal } from "./CreateWorkspaceModal";

function WorkspaceAvatar({
  name,
  color,
}: {
  name: string;
  color?: string | null;
}) {
  const initials = name.slice(0, 2).toUpperCase();
  return (
    <div
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[10px] font-bold text-white"
      style={{ backgroundColor: color ?? "var(--color-accent)" }}
    >
      {initials}
    </div>
  );
}

function useRoleLabel(role: string): string {
  const { t } = useTranslation("settings");
  const labels: Record<string, string> = {
    owner: t("workspace.roles.owner"),
    admin: t("workspace.roles.admin"),
    member: t("workspace.roles.member"),
    auditor: t("workspace.roles.auditor"),
    viewer: t("workspace.roles.viewer"),
  };
  return labels[role] ?? role;
}

function RoleBadge({ role }: { role: string }) {
  const label = useRoleLabel(role);
  return (
    <span className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium capitalize text-muted bg-muted/20">
      {label}
    </span>
  );
}

export function WorkspaceSelector({ currentSlug }: { currentSlug: string }) {
  const { t } = useTranslation("settings");
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const workspaces = useQuery(api.workspaces.getMyWorkspaces, {});
  const limitInfo = useQuery(api.billing.queries.canCreateWorkspace, {});
  const navigate = useNavigate();

  const currentWorkspace = (workspaces ?? []).find(
    (ws) => ws.slug === currentSlug,
  );

  const canCreate = limitInfo?.canCreate ?? false;

  return (
    <div className="mb-6">
      <span className="text-[12px] font-medium text-secondary block mb-1.5">
        {t("workspace.selector.label")}
      </span>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className="flex w-full max-w-sm items-center gap-2.5 rounded-lg border border-border px-3 py-2 hover:border-accent/40 transition-colors duration-150"
          >
            {currentWorkspace && (
              <WorkspaceAvatar
                name={currentWorkspace.name ?? ""}
                color={currentWorkspace.accentColor ?? undefined}
              />
            )}
            <span className="flex-1 truncate text-left text-[13px] font-medium text-primary">
              {currentWorkspace?.name ?? ""}
            </span>
            <ChevronDown size={14} className="shrink-0 text-muted" />
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={4}
            className="z-50 w-[280px] rounded-lg border border-border/60 bg-surface p-1 shadow-lg animate-in fade-in-0 zoom-in-95"
          >
            {(workspaces ?? []).map((ws) => {
              const isCurrent = ws.slug === currentSlug;
              return (
                <button
                  key={ws._id}
                  type="button"
                  onClick={() => {
                    if (!isCurrent) {
                      navigate({
                        to: "/$workspaceSlug/settings/workspace",
                        params: { workspaceSlug: ws.slug },
                      });
                    }
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-md px-2 py-2 transition-colors duration-150",
                    isCurrent
                      ? "border border-accent/50 bg-accent/5"
                      : "border border-transparent hover:bg-card-hover/50",
                  )}
                >
                  <WorkspaceAvatar
                    name={ws.name ?? ""}
                    color={ws.accentColor ?? undefined}
                  />
                  <span className="flex-1 truncate text-left text-[13px] font-medium text-primary">
                    {ws.name}
                  </span>
                  <RoleBadge role={ws.role} />
                </button>
              );
            })}

            <div className="mt-1 border-t border-border/40 pt-1">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setModalOpen(true);
                }}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] font-medium text-accent hover:bg-card-hover/50 transition-colors duration-150"
              >
                <Plus size={14} className="shrink-0" />
                {t("workspace.create.trigger")}
              </button>
              {!canCreate && (
                <p className="px-2 py-1 text-[11px] text-muted leading-snug">
                  <Trans
                    i18nKey="workspace.create.limitReached"
                    ns="settings"
                    values={{
                      count:
                        typeof limitInfo?.maxWorkspaces === "number"
                          ? limitInfo.maxWorkspaces
                          : 1,
                    }}
                    components={{
                      upgrade: (
                        <button
                          type="button"
                          onClick={() => {
                            setOpen(false);
                            navigate({
                              to: "/$workspaceSlug/settings/billing",
                              params: { workspaceSlug: currentSlug },
                            });
                          }}
                          className="font-medium text-accent hover:underline"
                        />
                      ),
                    }}
                  />
                </p>
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      <CreateWorkspaceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}
